import type { BeraterAnswer, BeraterRecommendation, ResolvedItem } from "./types";

export type BeraterEntrySource = "menu" | "pdp" | "themenwelt" | "paid" | "organic" | "direct" | "other";
export type BeraterFlowShort = "bett" | "matratzen";
export type BeraterAtcType = "single" | "set";
export type BeraterItemRole = "main" | "accessory" | "alternative";

const ANALYTICS_PREFIX = "ek:berater:";
const REF_WHITELIST = new Set(["menu", "pdp", "themenwelt", "paid"]);
const PAID_CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid"];
const PAID_MEDIUM = /^(cpc|ppc|paid|display|cpm|retargeting)$/i;
const SEARCH_HOST_FRAGMENTS = ["google.", "bing.", "duckduckgo.", "ecosia.", "yahoo.", "baidu."];

interface StoredEntry {
  source: BeraterEntrySource;
  referrer_host: string | null;
  has_click_id: boolean;
  utm: { source: string | null; medium: string | null; campaign: string | null };
}

interface BeraterEnvelope {
  berater_flow: BeraterFlowShort;
  berater_entry_source: BeraterEntrySource;
  berater_session_id: string;
  funnel_type: "berater";
  debug: boolean;
  page_location: string;
}

declare global {
  interface Window {
    Shopify?: {
      analytics?: { publish?: (name: string, data?: Record<string, unknown>) => void };
      currency?: { active?: string };
    };
  }
}

const entryStorageKey = (flowHandle: string): string => `${ANALYTICS_PREFIX}entry:${flowHandle}`;
const sessionIdStorageKey = (flowHandle: string): string => `${ANALYTICS_PREFIX}session_id:${flowHandle}`;
const sessionSentStorageKey = (flowHandle: string): string => `${ANALYTICS_PREFIX}session_sent:${flowHandle}`;
const attemptStorageKey = (flowHandle: string): string => `${ANALYTICS_PREFIX}attempt:${flowHandle}`;
const atcDoneStorageKey = (flowHandle: string): string => `${ANALYTICS_PREFIX}atc_done:${flowHandle}`;
const abandonSentStorageKey = (flowHandle: string): string => `${ANALYTICS_PREFIX}abandon_sent:${flowHandle}`;

const sentResultKeys = new Set<string>();
const sentLoadingKeys = new Set<string>();

export const flowShortFromHandle = (flowHandle: string): BeraterFlowShort =>
  flowHandle.includes("matratzen") ? "matratzen" : "bett";

const randomSessionId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
};

const readStoredEntry = (flowHandle: string): StoredEntry | null => {
  try {
    const raw = sessionStorage.getItem(entryStorageKey(flowHandle));
    if (!raw) return null;
    return JSON.parse(raw) as StoredEntry;
  } catch {
    return null;
  }
};

const writeStoredEntry = (flowHandle: string, entry: StoredEntry): void => {
  sessionStorage.setItem(entryStorageKey(flowHandle), JSON.stringify(entry));
};

const referrerHost = (referrer: string): string | null => {
  try {
    return new URL(referrer).hostname.replace(/^www\./, "") || null;
  } catch {
    return null;
  }
};

const isBeraterPagePath = (pathname: string): boolean => pathname.includes("-berater");

const isSearchHost = (host: string): boolean => {
  const normalized = host.toLowerCase().replace(/^www\./, "");
  for (const fragment of SEARCH_HOST_FRAGMENTS) {
    if (normalized.includes(fragment)) return true;
  }
  return false;
};

export const classifyEntrySource = (searchParams: URLSearchParams, referrer: string): StoredEntry => {
  const refParam = searchParams.get("ref")?.trim().toLowerCase() ?? "";
  if (refParam && REF_WHITELIST.has(refParam)) {
    return {
      source: refParam as BeraterEntrySource,
      referrer_host: referrer ? referrerHost(referrer) : null,
      has_click_id: PAID_CLICK_IDS.some((key) => searchParams.has(key)),
      utm: {
        source: searchParams.get("utm_source"),
        medium: searchParams.get("utm_medium"),
        campaign: searchParams.get("utm_campaign"),
      },
    };
  }

  if (refParam) {
    return {
      source: "other",
      referrer_host: referrer ? referrerHost(referrer) : null,
      has_click_id: false,
      utm: {
        source: searchParams.get("utm_source"),
        medium: searchParams.get("utm_medium"),
        campaign: searchParams.get("utm_campaign"),
      },
    };
  }

  const hasClickId = PAID_CLICK_IDS.some((key) => searchParams.has(key));
  if (hasClickId) {
    return {
      source: "paid",
      referrer_host: referrer ? referrerHost(referrer) : null,
      has_click_id: true,
      utm: {
        source: searchParams.get("utm_source"),
        medium: searchParams.get("utm_medium"),
        campaign: searchParams.get("utm_campaign"),
      },
    };
  }

  const utmMedium = searchParams.get("utm_medium")?.trim() ?? "";
  if (utmMedium && PAID_MEDIUM.test(utmMedium)) {
    return {
      source: "paid",
      referrer_host: referrer ? referrerHost(referrer) : null,
      has_click_id: false,
      utm: {
        source: searchParams.get("utm_source"),
        medium: utmMedium,
        campaign: searchParams.get("utm_campaign"),
      },
    };
  }

  if (referrer) {
    try {
      const refUrl = new URL(referrer);
      if (refUrl.origin === window.location.origin) {
        const path = refUrl.pathname;
        if (path.startsWith("/products/")) {
          return { source: "pdp", referrer_host: referrerHost(referrer), has_click_id: false, utm: { source: null, medium: null, campaign: null } };
        }
        if (path.startsWith("/collections/")) {
          return { source: "menu", referrer_host: referrerHost(referrer), has_click_id: false, utm: { source: null, medium: null, campaign: null } };
        }
        if (path.startsWith("/pages/") && !isBeraterPagePath(path)) {
          return { source: "themenwelt", referrer_host: referrerHost(referrer), has_click_id: false, utm: { source: null, medium: null, campaign: null } };
        }
        return { source: "menu", referrer_host: referrerHost(referrer), has_click_id: false, utm: { source: null, medium: null, campaign: null } };
      }
    } catch {
      // fall through
    }
  }

  if (referrer) {
    const host = referrerHost(referrer);
    if (host && isSearchHost(host)) {
      return { source: "organic", referrer_host: host, has_click_id: false, utm: { source: null, medium: null, campaign: null } };
    }
  }

  if (!referrer && ![...searchParams.keys()].length) {
    return { source: "direct", referrer_host: null, has_click_id: false, utm: { source: null, medium: null, campaign: null } };
  }

  return {
    source: "other",
    referrer_host: referrer ? referrerHost(referrer) : null,
    has_click_id: false,
    utm: {
      source: searchParams.get("utm_source"),
      medium: searchParams.get("utm_medium"),
      campaign: searchParams.get("utm_campaign"),
    },
  };
};

const ensureSessionId = (flowHandle: string): string => {
  const key = sessionIdStorageKey(flowHandle);
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = randomSessionId();
  sessionStorage.setItem(key, id);
  return id;
};

const getAttemptNo = (flowHandle: string): number => {
  const raw = sessionStorage.getItem(attemptStorageKey(flowHandle));
  const parsed = raw ? Number.parseInt(raw, 10) : 0;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const bumpAttemptNo = (flowHandle: string): number => {
  const next = getAttemptNo(flowHandle) + 1;
  sessionStorage.setItem(attemptStorageKey(flowHandle), String(next));
  return next;
};

const resetAttemptNo = (flowHandle: string): void => {
  sessionStorage.setItem(attemptStorageKey(flowHandle), "1");
};

const buildEnvelope = (flowHandle: string, debug: boolean): BeraterEnvelope => {
  const stored = readStoredEntry(flowHandle);
  if (!stored) {
    const classified = classifyEntrySource(new URLSearchParams(window.location.search), document.referrer);
    writeStoredEntry(flowHandle, classified);
  }
  const entry = readStoredEntry(flowHandle)!;
  return {
    berater_flow: flowShortFromHandle(flowHandle),
    berater_entry_source: entry.source,
    berater_session_id: ensureSessionId(flowHandle),
    funnel_type: "berater",
    debug: debug || new URLSearchParams(window.location.search).get("berater_debug") === "1",
    page_location: window.location.href,
  };
};

const publish = (name: string, data: Record<string, unknown>): void => {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch {
    // analytics unavailable
  }
};

export const initBeraterAnalyticsSession = (flowHandle: string, debug = false): void => {
  if (sessionStorage.getItem(sessionSentStorageKey(flowHandle)) === "1") return;

  if (!readStoredEntry(flowHandle)) {
    writeStoredEntry(flowHandle, classifyEntrySource(new URLSearchParams(window.location.search), document.referrer));
  }
  ensureSessionId(flowHandle);

  const entry = readStoredEntry(flowHandle)!;
  const envelope = buildEnvelope(flowHandle, debug);

  publish("berater_session", {
    ...envelope,
    entry_referrer_host: entry.referrer_host,
    has_click_id: entry.has_click_id,
    ...(entry.utm.source ? { utm_source: entry.utm.source } : {}),
    ...(entry.utm.medium ? { utm_medium: entry.utm.medium } : {}),
    ...(entry.utm.campaign ? { utm_campaign: entry.utm.campaign } : {}),
  });

  sessionStorage.setItem(sessionSentStorageKey(flowHandle), "1");
};

export const buildAnswerSummary = (answers: BeraterAnswer[]): Record<string, string | string[] | null> => {
  const summary: Record<string, string | string[] | null> = {
    age: null,
    size: null,
    bed_type: null,
    wood: null,
    material: null,
    color: null,
    accessories: [],
  };

  for (const answer of answers) {
    if (answer.attribute === "none") continue;
    if (answer.attribute === "accessories") {
      summary.accessories = answer.values;
      continue;
    }
    summary[answer.attribute] = answer.values.length === 1 ? answer.values[0] : answer.values;
  }

  return summary;
};

const activeCurrency = (): string => window.Shopify?.currency?.active ?? "EUR";

const itemName = (item: ResolvedItem): string =>
  item.detail ? `${item.product.title} / ${item.detail}` : item.product.title;

export const buildGa4Items = (
  items: ResolvedItem[],
  roles: BeraterItemRole[],
): Array<Record<string, string | number>> =>
  items.map((item, index) => ({
    item_id: item.variant.sku || String(item.variant.id),
    item_name: itemName(item),
    price: item.variant.price / 100,
    quantity: 1,
    item_role: roles[index] ?? (index === 0 ? "main" : "accessory"),
    item_variant: item.detail || item.variant.title,
    item_brand: "Ehrenkind",
  }));

const rolesForResolvedItems = (items: ResolvedItem[]): BeraterItemRole[] =>
  items.map((_, index) => (index === 0 ? "main" : "accessory"));

export const publishBeraterStarted = (flowHandle: string, debug: boolean, skipIntro: boolean): void => {
  if (!sessionStorage.getItem(attemptStorageKey(flowHandle))) {
    resetAttemptNo(flowHandle);
  }

  publish("berater_started", {
    ...buildEnvelope(flowHandle, debug),
    skip_intro: skipIntro,
    attempt_no: getAttemptNo(flowHandle),
  });
};

export const publishBeraterStepAnswered = (
  flowHandle: string,
  debug: boolean,
  payload: {
    stepHandle: string;
    stepAttribute: string;
    stepIndex: number;
    answerValues: string[];
    multiSelect: boolean;
  },
): void => {
  publish("berater_step_answered", {
    ...buildEnvelope(flowHandle, debug),
    step_handle: payload.stepHandle,
    step_attribute: payload.stepAttribute,
    step_index: payload.stepIndex,
    answer_values: payload.answerValues.join(","),
    multi_select: payload.multiSelect,
  });
};

export const publishBeraterLoadingViewed = (flowHandle: string, debug: boolean, answers: BeraterAnswer[], renderKey: string): void => {
  if (sentLoadingKeys.has(renderKey)) return;
  sentLoadingKeys.add(renderKey);

  publish("berater_loading_viewed", {
    ...buildEnvelope(flowHandle, debug),
    answer_summary: buildAnswerSummary(answers),
  });
};

export const publishBeraterEmptyResult = (flowHandle: string, debug: boolean, renderKey: string): void => {
  if (sentResultKeys.has(`${renderKey}:empty`)) return;
  sentResultKeys.add(`${renderKey}:empty`);

  publish("berater_empty_result", {
    ...buildEnvelope(flowHandle, debug),
  });
};

export const publishBeraterResultViewed = (
  flowHandle: string,
  debug: boolean,
  renderKey: string,
  recommendation: BeraterRecommendation,
  items: ResolvedItem[],
  alternativeCount: number,
  answers: BeraterAnswer[],
): void => {
  if (sentResultKeys.has(renderKey)) return;
  sentResultKeys.add(renderKey);

  const ga4Items = buildGa4Items(items, rolesForResolvedItems(items));
  const value = ga4Items.reduce((sum, row) => sum + Number(row.price) * Number(row.quantity), 0);
  const accessoryCount = Math.max(0, items.length - 1);

  publish("berater_result_viewed", {
    ...buildEnvelope(flowHandle, debug),
    berater_relaxed: recommendation.relaxed,
    dropped_criteria: recommendation.droppedCriteria,
    answer_summary: buildAnswerSummary(answers),
    accessory_count: accessoryCount,
    alternative_count: alternativeCount,
    ecommerce: {
      currency: activeCurrency(),
      value,
      items: ga4Items,
    },
  });
};

export const publishBeraterItemRemoved = (
  flowHandle: string,
  debug: boolean,
  removedItem: ResolvedItem,
  remainingItems: ResolvedItem[],
): void => {
  const ga4Items = buildGa4Items([removedItem], ["accessory"]);
  const value = ga4Items.reduce((sum, row) => sum + Number(row.price) * Number(row.quantity), 0);

  publish("berater_item_removed", {
    ...buildEnvelope(flowHandle, debug),
    variant_id: removedItem.variant.id,
    sku: removedItem.variant.sku || String(removedItem.variant.id),
    item_role: "accessory",
    remaining_item_count: remainingItems.length,
    remaining_accessory_count: Math.max(0, remainingItems.length - 1),
    ecommerce: {
      currency: activeCurrency(),
      value,
      items: ga4Items,
    },
  });
};

export const publishBeraterAddToCart = (
  flowHandle: string,
  debug: boolean,
  atcType: BeraterAtcType,
  relaxed: boolean,
  items: ResolvedItem[],
): void => {
  const ga4Items = buildGa4Items(items, rolesForResolvedItems(items));
  const value = ga4Items.reduce((sum, row) => sum + Number(row.price) * Number(row.quantity), 0);

  publish("berater_add_to_cart", {
    ...buildEnvelope(flowHandle, debug),
    berater_atc_type: atcType,
    berater_relaxed: relaxed,
    ecommerce: {
      currency: activeCurrency(),
      value,
      items: ga4Items,
    },
  });

  sessionStorage.setItem(atcDoneStorageKey(flowHandle), "1");
};

export const publishBeraterPdpClick = (
  flowHandle: string,
  debug: boolean,
  payload: { clickTarget: "winner" | "gallery" | "details_fallback"; productHandle: string; variantId?: number },
): void => {
  publish("berater_pdp_click", {
    ...buildEnvelope(flowHandle, debug),
    click_target: payload.clickTarget,
    product_handle: payload.productHandle,
    ...(payload.variantId ? { variant_id: payload.variantId } : {}),
  });
};

export const publishBeraterRestart = (flowHandle: string, debug: boolean, fromPanel: string): void => {
  const nextAttempt = bumpAttemptNo(flowHandle);
  publish("berater_restart", {
    ...buildEnvelope(flowHandle, debug),
    from_panel: fromPanel,
    attempt_no: nextAttempt,
  });
};

export const publishBeraterAbandon = (
  flowHandle: string,
  debug: boolean,
  payload: { lastPanel: string; lastStepIndex: number; reachedResult: boolean; exitMethod: "close_button" | "return_link" | "unload" },
): void => {
  if (sessionStorage.getItem(atcDoneStorageKey(flowHandle)) === "1") return;
  if (sessionStorage.getItem(abandonSentStorageKey(flowHandle)) === "1") return;

  publish("berater_abandon", {
    ...buildEnvelope(flowHandle, debug),
    last_panel: payload.lastPanel,
    last_step_index: payload.lastStepIndex,
    reached_result: payload.reachedResult,
    exit_method: payload.exitMethod,
  });

  sessionStorage.setItem(abandonSentStorageKey(flowHandle), "1");
};

export const beraterCartLineProperties = (
  flowHandle: string,
  atcType: BeraterAtcType,
  relaxed: boolean,
  itemRole: BeraterItemRole,
): Record<string, string> => {
  const entry = readStoredEntry(flowHandle);
  const envelope = buildEnvelope(flowHandle, false);
  return {
    _berater_flow: envelope.berater_flow,
    _berater_entry_source: entry?.source ?? envelope.berater_entry_source,
    _berater_atc_type: atcType,
    _berater_relaxed: String(relaxed),
    _berater_item_role: itemRole,
    _berater_session_id: envelope.berater_session_id,
  };
};

export const bindBeraterAbandonOnUnload = (flowHandle: string, debug: boolean, getState: () => { panel: string; stepIndex: number; reachedResult: boolean }): void => {
  const fire = (): void => {
    const state = getState();
    publishBeraterAbandon(flowHandle, debug, {
      lastPanel: state.panel,
      lastStepIndex: state.stepIndex,
      reachedResult: state.reachedResult,
      exitMethod: "unload",
    });
  };

  window.addEventListener("pagehide", fire);
};

// ponytail: O(1) self-check — fails if entry-source classifier regresses
const _paidCheck = classifyEntrySource(new URLSearchParams("gclid=abc&utm_medium=cpc"), "");
const _menuCheck = classifyEntrySource(new URLSearchParams("ref=menu"), "");
if (_paidCheck.source !== "paid" || _menuCheck.source !== "menu") {
  throw new Error("[ek-berater] analytics self-check failed");
}
